"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { MessageSquare, Mail, Phone, Clock, AlertTriangle, ShieldAlert, Rocket } from "lucide-react";
import type { EffectiveMaintenanceSettings } from "@/lib/maintenance";
import logoImg from "../../../public/brand/logo.png";

interface MaintenanceScreenProps {
  settings: EffectiveMaintenanceSettings;
  logoUrl?: string;
}

export function MaintenanceScreen({ settings, logoUrl }: MaintenanceScreenProps) {

  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  useEffect(() => {
    if (!settings.expectedReturnAt) return;

    const target = new Date(settings.expectedReturnAt).getTime();
    if (isNaN(target)) return;

    function updateTimer() {
      const now = new Date().getTime();
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    }

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [settings.expectedReturnAt]);

  const modeIcon =
    settings.mode === "coming_soon" ? (
      <Rocket size={32} className="maint-screen__badge-icon" />
    ) : settings.mode === "blocked" ? (
      <ShieldAlert size={32} className="maint-screen__badge-icon" />
    ) : (
      <AlertTriangle size={32} className="maint-screen__badge-icon" />
    );

  const waDigits = settings.contactWhatsapp.replace(/\D/g, "");
  const waHref = waDigits
    ? `https://wa.me/${waDigits}?text=${encodeURIComponent(
        `Olá! Estou tentando acessar o site e gostaria de falar com a equipe.`,
      )}`
    : null;

  return (
    <div className="maint-screen" data-mode={settings.mode} data-accent={settings.accentColor}>
      <div className="maint-screen__card">
        <header className="maint-screen__header">
          {logoUrl ? (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img src={logoUrl} alt="Global ERP" className="maint-screen__logo" style={{ height: 36, width: "auto" }} />
          ) : (
            <Image src={logoImg} alt="Global ERP" height={36} className="maint-screen__logo" />
          )}
        </header>


        <div className="maint-screen__badge">
          {modeIcon}
          <span>{settings.badgeText}</span>
        </div>

        <h1 className="maint-screen__title">{settings.headline}</h1>
        <p className="maint-screen__text">{settings.subheadline}</p>

        {timeLeft && (
          <div className="maint-screen__timer">
            <div className="maint-screen__timer-title">
              <Clock size={16} /> Previsão de Retorno:
            </div>
            <div className="maint-screen__timer-grid">
              <div className="maint-screen__timer-unit">
                <span className="maint-screen__timer-num">{timeLeft.days}</span>
                <span className="maint-screen__timer-lbl">dias</span>
              </div>
              <div className="maint-screen__timer-unit">
                <span className="maint-screen__timer-num">
                  {String(timeLeft.hours).padStart(2, "0")}
                </span>
                <span className="maint-screen__timer-lbl">horas</span>
              </div>
              <div className="maint-screen__timer-unit">
                <span className="maint-screen__timer-num">
                  {String(timeLeft.minutes).padStart(2, "0")}
                </span>
                <span className="maint-screen__timer-lbl">min</span>
              </div>
              <div className="maint-screen__timer-unit">
                <span className="maint-screen__timer-num">
                  {String(timeLeft.seconds).padStart(2, "0")}
                </span>
                <span className="maint-screen__timer-lbl">seg</span>
              </div>
            </div>
          </div>
        )}

        {settings.showContactBtn && (
          <div className="maint-screen__actions">
            {waHref ? (
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary maint-screen__btn"
              >
                <MessageSquare size={18} /> {settings.ctaLabel || "Falar no WhatsApp"}
              </a>
            ) : settings.ctaHref ? (
              <Link href={settings.ctaHref} className="btn btn--primary maint-screen__btn">
                {settings.ctaLabel || "Entrar em Contato"}
              </Link>
            ) : null}

            {settings.contactEmail && (
              <a
                href={`mailto:${settings.contactEmail}`}
                className="btn btn--outline maint-screen__btn"
              >
                <Mail size={18} /> {settings.contactEmail}
              </a>
            )}

            {settings.contactPhone && (
              <a
                href={`tel:${settings.contactPhone.replace(/\D/g, "")}`}
                className="btn btn--outline maint-screen__btn"
              >
                <Phone size={18} /> {settings.contactPhone}
              </a>
            )}
          </div>
        )}

        <footer className="maint-screen__footer">
          <span>&copy; {new Date().getFullYear()} Global ERP Tecnologia Empresarial</span>
        </footer>
      </div>
    </div>
  );
}
